import axios from "axios";
import { hideChildModal, hideModal } from "../data/modal";

/*****************/
/* INITIAL STATE */
/*****************/
const initialState = {
  loading: false,
  error: "",
  data: [],
  query: { status: null, total: 0 },
};

/*********/
/* TYPES */
/*********/
const FETCH_VOYAGES = "FETCH_VOYAGES";
const FETCH_VOYAGES_SUCCESS = "FETCH_VOYAGES_SUCCESS";
const FETCH_VOYAGES_ERROR = "FETCH_VOYAGES_ERROR";
const FETCH_NEXT_VOYAGES = "FETCH_NEXT_VOYAGES";
const FETCH_NEXT_VOYAGES_SUCCESS = "FETCH_NEXT_VOYAGES_SUCCESS";
const FETCH_VOYAGE = "FETCH_VOYAGE";
const FETCH_VOYAGE_SUCCESS = "FETCH_VOYAGE_SUCCESS";
const FETCH_VOYAGE_ERROR = "FETCH_VOYAGE_ERROR";
const POST_VOYAGE = "POST_VOYAGE";
const POST_VOYAGE_ERROR = "POST_VOYAGE_ERROR";
const UPDATE_VOYAGE = "UPDATE_VOYAGE";
const UPDATE_VOYAGE_SUCCESS = "UPDATE_VOYAGE_SUCCESS";
const UPDATE_VOYAGE_ERROR = "UPDATE_VOYAGE_ERROR";

const getStatusParam = (status) =>
  status ? `?status=${encodeURIComponent(status)}` : "";

/*******************/
/* ACTION CREATORS */
/*******************/
export const getVoyages =
  (status = "active") =>
  async (dispatch, getState) => {
    const validStatus =
      typeof status === "string" && status.trim() !== "" ? status : "";

    if (!validStatus) {
      dispatch({
        type: FETCH_VOYAGES_SUCCESS,
        payload: { voyages: [], query: { status: "", total: 0 } },
      });
      return;
    }

    const {
      voyages: { query, data },
    } = getState();

    const isSameQuery = query.status === validStatus;
    const isDataComplete = data.length === query.total;

    if (!isSameQuery || !isDataComplete) {
      dispatch({ type: FETCH_VOYAGES });

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/voyages${getStatusParam(validStatus)}`,
          {
            headers: {
              "Content-Type": "application/ld+json; charset=utf-8",
              Accept: "application/ld+json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const members = response.data["hydra:member"] || [];
        const total = response.data["hydra:totalItems"] || 0;
        const next = response.data["hydra:view"]?.["hydra:next"];

        dispatch({
          type: FETCH_VOYAGES_SUCCESS,
          payload: { voyages: members, query: { status: validStatus, total } },
        });

        if (next) dispatch(getNextVoyages(next));
      } catch (err) {
        dispatch({ type: FETCH_VOYAGES_ERROR, payload: err.message });
      }
    }
  };

export const getNextVoyages = (nextUrl) => async (dispatch) => {
  dispatch({ type: FETCH_NEXT_VOYAGES });

  try {
    const response = await axios.get(nextUrl, {
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Accept: "application/ld+json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    dispatch({
      type: FETCH_NEXT_VOYAGES_SUCCESS,
      payload: response.data["hydra:member"] || [],
    });

    const next = response.data["hydra:view"]?.["hydra:next"];
    if (next) dispatch(getNextVoyages(next));
  } catch (err) {
    dispatch({ type: FETCH_VOYAGES_ERROR, payload: err.message });
  }
};

export const getVoyage = (voyageIdOrIRI) => async (dispatch) => {
  dispatch({ type: FETCH_VOYAGE });

  try {
    const id =
      typeof voyageIdOrIRI === "string"
        ? voyageIdOrIRI.replace("/api/voyages/", "")
        : voyageIdOrIRI;

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/voyages/${id}`,
      {
        headers: {
          "Content-Type": "application/ld+json; charset=utf-8",
          Accept: "application/ld+json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    dispatch({ type: FETCH_VOYAGE_SUCCESS, payload: response.data });
    return response.data;
  } catch (err) {
    dispatch({ type: FETCH_VOYAGE_ERROR, payload: err.message });
  }
};

export const createVoyage = (voyage, allowHideModal = true) => async (dispatch, getState) => {
  dispatch({ type: POST_VOYAGE });
  const {
    voyages: { data, query },
    modal,
  } = getState();

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/voyages`,
      voyage,
      {
        headers: {
          "Content-Type": "application/ld+json; charset=utf-8",
          Accept: "application/ld+json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (response.status === 201) {
      const newData = [...data, response.data];
      dispatch({ type: FETCH_VOYAGES_SUCCESS, payload: { voyages: newData, query } });

      if (allowHideModal) {
        if (modal.childModal) {
          dispatch(hideChildModal());
        } else {
          dispatch(hideModal());
        }
      }
    } else {
      dispatch({ type: POST_VOYAGE_ERROR, payload: 'Error creating voyage' });
    }
  } catch (err) {
    dispatch({ type: POST_VOYAGE_ERROR, payload: err.message });
  }
};

export const updateVoyage =
  (voyage, allowHideModal = true) =>
  async (dispatch, getState) => {
    dispatch({ type: UPDATE_VOYAGE });

    const { modal } = getState();

    try {
      const id = voyage["@id"].split("/").pop();
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/voyages/${id}`,
        voyage,
        {
          headers: {
            "Content-Type": "application/ld+json",
            Accept: "application/ld+json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      dispatch({ type: UPDATE_VOYAGE_SUCCESS, payload: response.data });

      if (allowHideModal) {
        if (modal.childModal) {
          dispatch(hideChildModal());
        } else {
          dispatch(hideModal());
        }
      }
    } catch (err) {
      dispatch({ type: UPDATE_VOYAGE_ERROR, payload: err.message });
    }
  };

export const loadVoyages = () => ({ type: FETCH_VOYAGES });
export const loadNextVoyages = () => ({ type: FETCH_NEXT_VOYAGES });
export const setError = (msg) => ({ type: FETCH_VOYAGES_ERROR, payload: msg });

/***********/
/* REDUCER */
/***********/
const voyageReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case FETCH_VOYAGES:
      return { ...state, loading: true, error: "" };
    case FETCH_VOYAGES_SUCCESS:
      return {
        ...state,
        loading: false,
        error: "",
        data: payload.voyages,
        query: payload.query,
      };
    case FETCH_VOYAGES_ERROR:
      return { ...state, loading: false, error: payload };
    case FETCH_NEXT_VOYAGES:
      return { ...state, error: "" };
    case FETCH_NEXT_VOYAGES_SUCCESS: {
      const existingIds = new Set(state.data.map((v) => v["@id"]));
      const uniqueNewItems = payload.filter((v) => !existingIds.has(v["@id"]));

      return { ...state, loading: false, data: [...state.data, ...uniqueNewItems] };
    }
    case FETCH_VOYAGE:
      return { ...state, loading: true, error: "" };
    case FETCH_VOYAGE_SUCCESS: {
      const updated = state.data.map((v) =>
        v["@id"] === payload["@id"] ? payload : v
      );
      const notFound = !state.data.some((v) => v["@id"] === payload["@id"]);
      return {
        ...state,
        loading: false,
        data: notFound ? [...state.data, payload] : updated,
      };
    }
    case FETCH_VOYAGE_ERROR:
      return { ...state, loading: false, error: payload };
    case POST_VOYAGE:
      return { ...state, loading: true, error: "" };
    case POST_VOYAGE_ERROR:
      return { ...state, loading: false, error: payload };
    case UPDATE_VOYAGE:
      return { ...state, loading: true, error: "" };
    case UPDATE_VOYAGE_SUCCESS:
      return {
        ...state,
        loading: false,
        data: state.data.map((v) =>
          v["@id"] === payload["@id"] ? payload : v
        ),
      };
    case UPDATE_VOYAGE_ERROR:
      return { ...state, loading: false, error: payload };
    default:
      return state;
  }
};

export default voyageReducer;
